/**
 * The item-density and cluster-density views.
 *
 * Two stages, exactly as in VOSviewer:
 *
 * 1. evaluate the field on a square grid (250x250 by default) — GPU by
 *    default, CPU when WebGL2 or float render targets are missing;
 * 2. turn the grid into a small RGBA image and let the browser scale it up
 *    smoothly.
 *
 * Stage 2 is where the two views part company.
 *
 * **Item density** normalises by the maximum of the all-items field and paints
 * every cell through the colour scheme, so the image is full-bleed and its
 * darkest colour is the scheme's value at zero, not the page background.
 *
 * **Cluster density** normalises by the maximum of *its own* field — the field
 * over items that belong to a cluster holding at least 2% of the total weight.
 * Items with no cluster are dropped, and so are the small clusters.  That means
 * **the two views' peaks are not comparable**: a hot spot in the cluster
 * density view can be dimmer or brighter than the same spot in the item density
 * view purely because the denominators differ.  VOSviewer does the same thing
 * and does not say so anywhere, which is how people end up reading a
 * cluster-density map as if it were an item-density map with colours.  It is
 * not; it answers "which cluster owns this region, and how strongly", and the
 * brightness is relative to the busiest clustered region on this map alone.
 */

import type { Bounds } from '../geometry/viewport';
import type { ColorScheme, RGB, Theme } from '../types';
import type { Scene } from '../model/scene';
import { buildLut, sampleScheme } from '../color/schemes';
import { labToRgb, rgbToLab } from '../color/lab';
import { COLOR_TRANSFORMATION, DENSITY_GRID_SIZE, ITEM_DENSITY_THRESHOLD } from '../model/constants';
import { GpuDensity, type DensityGrids } from './densityGpu';

export type DensityBackend = 'webgl' | 'cpu';

/**
 * VOSviewer's density-to-colour-strength curve:
 * `s = 1 - (1 - (d/dmax)^g)^(1/g)`, with `g = 0.55`.
 *
 * It is its own inverse about the diagonal, and at `g < 1` it lifts the low
 * end steeply: a cell at 10% of the peak comes out at about 38% of the ramp.
 * Without it a density map is a few bright dots on a flat dark field, because
 * a Gaussian sum is dominated by its peaks.
 */
export function colorStrength(ratio: number, g = COLOR_TRANSFORMATION): number {
  if (!(ratio > 0)) return 0;
  if (ratio >= 1) return 1;
  return 1 - Math.pow(1 - Math.pow(ratio, g), 1 / g);
}

/**
 * The field on the CPU, following `VOSviewer.calcDensities` including its
 * bounding-box optimisation.
 *
 * An item stops contributing once its kernel drops below
 * `ITEM_DENSITY_THRESHOLD`, which for a mean-weight item is about three kernel
 * widths out.  That turns an O(items * cells) loop into O(items * localCells)
 * and is why the CPU fallback is a real fallback rather than a slideshow: at
 * 2000 items and a 250-grid it is a few tens of milliseconds.
 */
export function computeGridsCpu(
  gridSize: number,
  extent: Bounds,
  bandwidth: number,
  itemDensityThreshold: number,
  x: Float64Array,
  y: Float64Array,
  normWeight: Float64Array,
  inLargeCluster: Uint8Array,
  clusterRgb: Uint8Array,
): DensityGrids {
  const cells = gridSize * gridSize;
  const item = new Float32Array(cells);
  const cluster = new Float32Array(cells * 4);
  const h2 = bandwidth * bandwidth;
  if (!(h2 > 0)) return { gridSize, item, cluster, itemMax: 0, clusterMax: 0 };

  const [xMin, yMin, xMax, yMax] = extent;
  const xStep = (xMax - xMin) / (gridSize - 1);
  const yStep = (yMax - yMin) / (gridSize - 1);
  const n = x.length;

  for (let k = 0; k < n; k += 1) {
    const w = normWeight[k] as number;
    if (!(w > 0)) continue;
    const logThreshold = Math.log(itemDensityThreshold / w);
    if (logThreshold >= 0) continue; // the item's own peak is below the floor
    const reach = Math.sqrt(-logThreshold * h2);
    const px = x[k] as number;
    const py = y[k] as number;
    const i1 = Math.max(Math.ceil((px - reach - xMin) / xStep), 0);
    const i2 = Math.min(Math.floor((px + reach - xMin) / xStep), gridSize - 1);
    const j1 = Math.max(Math.ceil((py - reach - yMin) / yStep), 0);
    const j2 = Math.min(Math.floor((py + reach - yMin) / yStep), gridSize - 1);
    const large = inLargeCluster[k] === 1;
    const cr = (clusterRgb[k * 3] as number) / 255;
    const cg = (clusterRgb[k * 3 + 1] as number) / 255;
    const cb = (clusterRgb[k * 3 + 2] as number) / 255;
    for (let j = j1; j <= j2; j += 1) {
      const dy = yMin + j * yStep - py;
      const dy2 = dy * dy;
      const row = j * gridSize;
      for (let i = i1; i <= i2; i += 1) {
        const dx = xMin + i * xStep - px;
        const logDensity = -(dx * dx + dy2) / h2;
        if (logDensity < logThreshold) continue;
        const contribution = w * Math.exp(logDensity);
        const cell = row + i;
        item[cell] = (item[cell] as number) + contribution;
        if (large) {
          cluster[cell * 4] = (cluster[cell * 4] as number) + cr * contribution;
          cluster[cell * 4 + 1] = (cluster[cell * 4 + 1] as number) + cg * contribution;
          cluster[cell * 4 + 2] = (cluster[cell * 4 + 2] as number) + cb * contribution;
          cluster[cell * 4 + 3] = (cluster[cell * 4 + 3] as number) + contribution;
        }
      }
    }
  }

  let itemMax = 0;
  let clusterMax = 0;
  for (let c = 0; c < cells; c += 1) {
    const v = item[c] as number;
    if (v > itemMax) itemMax = v;
    const a = cluster[c * 4 + 3] as number;
    if (a > clusterMax) clusterMax = a;
  }
  return { gridSize, item, cluster, itemMax, clusterMax };
}

export interface DensityConfig {
  gridSize: number;
  itemDensityThreshold: number;
  colorTransformation: number;
  scheme: ColorScheme;
  forceCpu: boolean;
}

export function defaultDensityConfig(scheme: ColorScheme): DensityConfig {
  return {
    gridSize: DENSITY_GRID_SIZE,
    itemDensityThreshold: ITEM_DENSITY_THRESHOLD,
    colorTransformation: COLOR_TRANSFORMATION,
    scheme,
    forceCpu: false,
  };
}

/**
 * Owns the field, the two tinted images and the WebGL context behind them.
 *
 * The expensive work is keyed on the scene: `setScene` recomputes the grid,
 * `imageFor` recolours it (cheap — 62 500 cells), and `draw` is a single
 * `drawImage`.  Panning and zooming only ever reach `draw`.
 */
export class DensityLayer {
  private config: DensityConfig;

  private gpu: GpuDensity | null = null;

  private gpuUnavailable = false;

  private grids: DensityGrids | null = null;

  private scene: Scene | null = null;

  private itemCanvas: HTMLCanvasElement | null = null;

  private clusterCanvas: HTMLCanvasElement | null = null;

  private tintedFor: { mode: string; theme: string; scheme: ColorScheme; g: number } | null = null;

  /** Which backend actually produced the current field. Surfaced for diagnostics. */
  backend: DensityBackend = 'cpu';

  constructor(config: DensityConfig) {
    this.config = config;
  }

  setConfig(config: DensityConfig): void {
    const gridChanged = config.gridSize !== this.config.gridSize;
    const fieldChanged =
      gridChanged ||
      config.itemDensityThreshold !== this.config.itemDensityThreshold ||
      config.forceCpu !== this.config.forceCpu;
    this.config = config;
    this.tintedFor = null;
    if (fieldChanged) this.grids = null;
  }

  /**
   * Adopt a scene.  The field is *not* computed here: a user who never opens a
   * density view should not pay for one, so the grid is built on the first
   * draw and cached until the scene or the configuration changes.
   */
  setScene(scene: Scene): void {
    this.scene = scene;
    this.grids = null;
    this.tintedFor = null;
  }

  private ensureGrids(): void {
    if (this.grids) return;
    const scene = this.scene;
    if (!scene || scene.n === 0) return;

    const { gridSize, itemDensityThreshold } = this.config;
    if (!this.config.forceCpu && !this.gpuUnavailable) {
      try {
        this.gpu ??= new GpuDensity();
        this.grids = this.gpu.compute(
          gridSize,
          scene.densityExtent,
          scene.bandwidth,
          itemDensityThreshold,
          scene.x,
          scene.y,
          scene.normWeight,
          scene.inLargeCluster,
          scene.clusterRgb,
        );
        this.backend = 'webgl';
      } catch (error) {
        // Any GL failure — no WebGL2, no float targets, a lost context, a
        // driver that refuses the float readback — drops us onto the CPU for
        // the rest of the session. The field is identical either way, so this
        // is a performance cliff rather than a break, and a silent performance
        // cliff is the kind nobody ever finds. Say so once.
        console.warn('[journalmap/render] density falling back to the CPU:', error);
        this.gpuUnavailable = true;
        this.gpu?.destroy();
        this.gpu = null;
        this.grids = null;
      }
    }
    if (!this.grids) {
      this.grids = computeGridsCpu(
        gridSize,
        scene.densityExtent,
        scene.bandwidth,
        itemDensityThreshold,
        scene.x,
        scene.y,
        scene.normWeight,
        scene.inLargeCluster,
        scene.clusterRgb,
      );
      this.backend = 'cpu';
    }
  }

  /** The colour the page should sit on behind this view. */
  backgroundFor(mode: 'item-density' | 'cluster-density', theme: Theme): RGB {
    // Item density fills its whole extent with the ramp, so anything outside
    // the extent should be the ramp's zero colour rather than a hard rectangle
    // edge. Cluster density genuinely has a background, and it is black.
    return mode === 'item-density' ? sampleScheme(this.config.scheme, 0) : theme.densityBackground;
  }

  private tint(mode: 'item-density' | 'cluster-density', theme: Theme): HTMLCanvasElement | null {
    this.ensureGrids();
    const grids = this.grids;
    if (!grids) return null;
    const key = {
      mode,
      theme: theme.name,
      scheme: this.config.scheme,
      g: this.config.colorTransformation,
    };
    const cached = mode === 'item-density' ? this.itemCanvas : this.clusterCanvas;
    if (
      cached &&
      this.tintedFor &&
      this.tintedFor.mode === key.mode &&
      this.tintedFor.theme === key.theme &&
      this.tintedFor.scheme === key.scheme &&
      this.tintedFor.g === key.g
    ) {
      return cached;
    }

    const size = grids.gridSize;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const image = ctx.createImageData(size, size);
    const data = image.data;
    const g = this.config.colorTransformation;

    if (mode === 'item-density') {
      const lut = buildLut(this.config.scheme, 256);
      const max = grids.itemMax;
      for (let j = 0; j < size; j += 1) {
        // Grid row 0 is the bottom of the extent; canvas row 0 is the top.
        const dstRow = (size - 1 - j) * size;
        const srcRow = j * size;
        for (let i = 0; i < size; i += 1) {
          const s = max > 0 ? colorStrength((grids.item[srcRow + i] as number) / max, g) : 0;
          const entry = Math.min(255, Math.max(0, Math.round(s * 255))) * 4;
          const o = (dstRow + i) * 4;
          data[o] = lut[entry] as number;
          data[o + 1] = lut[entry + 1] as number;
          data[o + 2] = lut[entry + 2] as number;
          data[o + 3] = 255;
        }
      }
      this.itemCanvas = canvas;
    } else {
      const background = theme.densityBackground;
      const bgLab = rgbToLab(background);
      const max = grids.clusterMax;
      const cluster = grids.cluster;
      for (let j = 0; j < size; j += 1) {
        const dstRow = (size - 1 - j) * size;
        const srcRow = j * size;
        for (let i = 0; i < size; i += 1) {
          const cell = (srcRow + i) * 4;
          const density = cluster[cell + 3] as number;
          const o = (dstRow + i) * 4;
          if (!(density > 0) || !(max > 0)) {
            data[o] = background[0];
            data[o + 1] = background[1];
            data[o + 2] = background[2];
            data[o + 3] = 255;
            continue;
          }
          // Hue = density-weighted mean of the contributing cluster colours.
          const hue: RGB = [
            ((cluster[cell] as number) / density) * 255,
            ((cluster[cell + 1] as number) / density) * 255,
            ((cluster[cell + 2] as number) / density) * 255,
          ];
          const s = colorStrength(density / max, g);
          // Blend from the background towards that hue by the strength — in
          // Lab, so a half-strength red is a dark red rather than a muddy brown.
          const out =
            s >= 1
              ? hue
              : labToRgb(mix(bgLab, rgbToLab(hue), s));
          data[o] = out[0];
          data[o + 1] = out[1];
          data[o + 2] = out[2];
          data[o + 3] = 255;
        }
      }
      this.clusterCanvas = canvas;
    }

    ctx.putImageData(image, 0, 0);
    this.tintedFor = key;
    return canvas;
  }

  /**
   * Blit the field into `ctx`, mapping the density extent onto the given
   * world-to-screen projection.
   *
   * The image is drawn across the extent *expanded by half a cell on each
   * side*, because a grid node sits at the node position while an image pixel
   * covers a cell.  Without the half cell the field is offset by 0.2% of the
   * map; with it, node centres land where the maths says they do.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    mode: 'item-density' | 'cluster-density',
    theme: Theme,
    project: (x: number, y: number) => [number, number],
  ): void {
    const scene = this.scene;
    const image = this.tint(mode, theme);
    if (!scene || !image) return;
    const [xMin, yMin, xMax, yMax] = scene.densityExtent;
    const size = image.width;
    const halfCellX = (xMax - xMin) / (size - 1) / 2;
    const halfCellY = (yMax - yMin) / (size - 1) / 2;
    const [left, bottom] = project(xMin - halfCellX, yMin - halfCellY);
    const [right, top] = project(xMax + halfCellX, yMax + halfCellY);
    const previousSmoothing = ctx.imageSmoothingEnabled;
    const previousQuality = ctx.imageSmoothingQuality;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, left, top, right - left, bottom - top);
    ctx.imageSmoothingEnabled = previousSmoothing;
    ctx.imageSmoothingQuality = previousQuality;
  }

  destroy(): void {
    this.gpu?.destroy();
    this.gpu = null;
    this.grids = null;
    this.itemCanvas = null;
    this.clusterCanvas = null;
    this.scene = null;
  }
}

function mix(a: readonly [number, number, number], b: readonly [number, number, number], t: number): [number, number, number] {
  const inv = 1 - t;
  return [a[0] * inv + b[0] * t, a[1] * inv + b[1] * t, a[2] * inv + b[2] * t];
}
